import React, { useEffect, useRef, useState } from 'react';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { ComposableMap, Geographies, Sphere, Graticule } from 'react-simple-maps';
import { maxBy } from 'lodash';
import LocationGeography from './LocationGeography';
import LocationTooltip from './LocationTooltip';
import { MAX_OPACITY, MIN_OPACITY } from './constants/LocationConstants';
import constants from '@shared-styles/constants.module.scss';
import { getPluralSuffix } from '../../../../../utils/plurals';

/**
 * @typedef {object} LocationSectionLocationsOrgUnits
 * @property {string} name
 * @property {number} fei
 */

/**
 * @typedef {object} LocationSectionLocations
 * @property {string} locationId
 * @property {number} rank
 * @property {LocationSectionLocationsOrgUnits[]} [orgUnits]
 */

/**
 * @typedef {object} LocationSectionProps
 * @property {LocationSectionLocations[]} [locations]
 * @property {number} [totalUnitCount]
 * @property {boolean} [loading]
 */

/**
 * @param {LocationSectionProps} props
 */
const LocationSection = props => {
  const { locations = [], totalUnitCount, loading = false } = props;

  const [geoData, setGeoData] = useState(null);

  const tooltipRef = useRef(null);

  useEffect(() => {
    const loadGeoData = async () => {
      const { default: data } = await import('./assets/country.json'); // For more details on JSON data, refer to: https://isaca-jira.atlassian.net/wiki/x/BQCr5
      setGeoData(data);
    };

    loadGeoData();
  }, []);

  const maxRank = maxBy(locations, x => x.rank)?.rank;
  const opacityPerRank = maxRank ? (MAX_OPACITY - MIN_OPACITY) / maxRank : 0;

  return (
    <section className='location-section'>
      <SkeletonWrapper show={loading}>
        <h4>{`Locations of ${totalUnitCount} Site${getPluralSuffix(totalUnitCount)}`}</h4>
      </SkeletonWrapper>

      <div className='map-chart-wrapper' data-testid='map-chart-wrapper'>
        {/* For more details on map projection config, refer to: https://www.react-simple-maps.io/docs/composable-map/ */}
        <ComposableMap
          projectionConfig={{
            rotate: [-10, 0, 0],
            scale: 130
          }}
          width={800}
          height={400}>
          <Sphere
            id='rsm-sphere'
            fill='transparent'
            stroke={constants.borderColor}
            strokeWidth={0.3}
          />
          <Graticule stroke={constants.borderColor} strokeWidth={0.3} />
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map(geography => {
                const locationData = locations.find(x => x.locationId === geography.id);

                return (
                  <LocationGeography
                    key={geography.rsmKey}
                    geography={geography}
                    locationData={locationData}
                    opacityPerRank={opacityPerRank}
                    tooltipRef={tooltipRef}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
        <LocationTooltip ref={tooltipRef} />
      </div>
    </section>
  );
};

export default LocationSection;
